import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useMutation } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Form, Formik, FormikProps, useFormikContext } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { DatePicker } from 'src/components/DatePicker';
import { Grid } from 'src/components/Grid';
import { Icon } from 'src/components/Icon';
import { Stack } from 'src/components/Stack';
import { Tooltip } from 'src/components/Tooltip';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import {
  ContractEndBehavior,
  ContractEndBehaviorDisplay,
  ProrationBehavior,
  ProrationBehaviorDisplay,
  StripePlan,
  SubscriptionInterval,
  SubscriptionIntervalDisplay,
} from 'src/models/payment/_types';
import { createStripePlan, updateStripePlan } from 'src/models/payment/payment';
import { FeatureFlag } from 'src/models/platform/_types';
import { FormCheckbox } from 'src/shared/Forms/Fields/FormCheckbox';
import { StripeTaxCodes } from 'src/shared/StripeTaxCodes';
import { hasFeatureFlag } from 'src/shared/usePermissionCheck';
import { formatOrdinal } from 'src/utils/numberFormatter';
import * as Yup from 'yup';

import { FormTextField } from './Fields/FormTextField';

interface SubscriptionPlanFormProps {
  plan?: Partial<StripePlan>;
  afterSave?: (values: Partial<StripePlan>) => Promise<void>;
  children: (formikProps: FormikProps<Partial<StripePlan>>) => React.ReactNode;
  isEditing?: boolean;
}

interface SubscriptionPlanFormFieldsProps {
  isEditing?: boolean;
}

export const SubscriptionPlanFormFields: React.FC<
  SubscriptionPlanFormFieldsProps
> = ({ isEditing = false }) => {
  const { values, setFieldValue } = useFormikContext<Partial<StripePlan>>();
  const { session } = useSession();
  const { t } = useTranslation('translation', {
    keyPrefix: 'paymentPlans.createDialog',
  });
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleIntervalChange = (interval: string) => {
    const count = values.intervalCount || 1;
    let newCount = count;

    if (interval === 'month' && count > 12) {
      newCount = 12;
    } else if (interval === 'day' && count > 365) {
      newCount = 365;
    } else if (interval === 'week' && count > 52) {
      newCount = 52;
    } else if (interval === 'year' && count > 1) {
      newCount = 1;
    }

    setFieldValue('interval', interval);
    if (newCount !== count) {
      setFieldValue('intervalCount', newCount);
    }
  };

  const handleIntervalCountChange = (count: number) => {
    const maxValues = {
      month: 36,
      day: 1095,
      week: 156,
      year: 3,
    };

    const maxCount = maxValues[values.interval as keyof typeof maxValues] || 0;
    const newCount = Math.min(count, maxCount);

    setFieldValue('intervalCount', newCount);
  };

  const handleAnchorChange = (date: Date | null) => {
    let value: string | null = null;
    if (date) {
      value = format(date, 'MM-dd');
    }
    setFieldValue('metadata.billingCycleAnchor', value);
  };

  // Convert string format "08-22" to Date object for year intervals
  const getBillingCycleAnchorAsDate = (): Date | null => {
    const anchorValue = values.metadata?.billingCycleAnchor;
    if (!anchorValue || values.interval !== 'year') {
      return null;
    }

    // Parse "08-22" format to Date
    const [month, day] = anchorValue.split('-').map(Number);
    if (month && day) {
      // Use current year for the date picker
      const currentYear = new Date().getFullYear();
      return new Date(currentYear, month - 1, day); // month is 0-indexed
    }

    return null;
  };

  return (
    <Stack spacing={3}>
      {!values.metadata?.oneOffPaymentPlan && (
        <Stack direction="row" spacing={2}>
          <FormTextField
            name="name"
            label={t('name')}
            placeholder={t('namePlaceholder')}
          />
          <FormTextField
            name="id"
            label={t('planId')}
            placeholder={t('planIdPlaceholder')}
            maxLength={22}
            disabled={isEditing}
          />
        </Stack>
      )}

      <Stack direction="row" spacing={2}>
        <FormTextField
          name="amount"
          label={t('amount')}
          type="number"
          min={0}
          placeholder={t('amountPlaceholder')}
          disabled={isEditing}
        />

        <Stack direction="row" spacing={2}>
          <FormTextField
            name="intervalCount"
            label={t('intervalCount')}
            type="number"
            min={0}
            placeholder={t('intervalCountPlaceholder')}
            onChange={(e) => handleIntervalCountChange(Number(e.target.value))}
            disabled={isEditing}
          />
          <FormTextField
            name="interval"
            label={t('interval')}
            options={intervalOptions}
            onChange={(e) => handleIntervalChange(e.target.value)}
            disabled={isEditing}
          />
        </Stack>
      </Stack>

      {!values.metadata?.oneOffPaymentPlan && (
        <FormTextField
          name="trialPeriodDays"
          label={t('trialPeriod')}
          type="number"
          min={0}
          placeholder={t('trialPeriodPlaceholder')}
        />
      )}

      <Button
        variant="text"
        onClick={() => setShowAdvanced(!showAdvanced)}
        startIcon={
          showAdvanced ? (
            <Icon icon={icon({ name: 'chevron-up' })} />
          ) : (
            <Icon icon={icon({ name: 'chevron-down' })} />
          )
        }
      >
        {showAdvanced ? t('hideAdvanced') : t('showAdvanced')}
      </Button>

      {showAdvanced && (
        <>
          <FormTextField
            name="statementDescriptor"
            label={t('statementDescriptor')}
            placeholder={t('statementDescriptorPlaceholder')}
            maxLength={22}
          />

          {hasFeatureFlag(session, FeatureFlag.StripeTax) && (
            <Stack spacing={2}>
              <Typography variant="subtitle1">{t('taxCode')}</Typography>

              <StripeTaxCodes
                initialTaxCode={(values as any).taxCodeId}
                onTaxCodeSelect={(taxCode) => {
                  setFieldValue('taxCodeId', taxCode);
                }}
              />

              <FormCheckbox
                name="metadata.disableStripeTax"
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    {t('disableStripeTax')}
                    <Tooltip title={t('disableStripeTaxTooltip')}>
                      <Icon
                        icon={icon({
                          name: 'question-circle',
                          style: 'regular',
                        })}
                      />
                    </Tooltip>
                  </Box>
                }
              />
            </Stack>
          )}

          {!values.metadata?.oneOffPaymentPlan && (
            <>
              {values.interval === 'year' ? (
                <DatePicker
                  label={t('billingCycleAnchor')}
                  helperText={t('billingCycleAnchorHelper')}
                  value={getBillingCycleAnchorAsDate()}
                  onChange={(value) => handleAnchorChange(value)}
                  slotProps={{
                    textField: {
                      placeholder: t('noAnchor'),
                    },
                  }}
                />
              ) : (
                <FormTextField
                  name="metadata.billingCycleAnchor"
                  label={t('billingCycleAnchor')}
                  helperText={t('billingCycleAnchorHelper')}
                  placeholder={t('noAnchor')}
                  options={getAnchorValues(values.interval, t)}
                />
              )}

              {!values.trialPeriodDays &&
                values.metadata?.billingCycleAnchor && (
                  <FormTextField
                    name="metadata.prorate"
                    label={t('prorationBehavior')}
                    helperText={getBehaviorText(values.metadata?.prorate, t)}
                    options={behaviors}
                  />
                )}
            </>
          )}

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormTextField
                name="metadata.cancelAfter"
                label={t('contractCycles')}
                type="number"
                min={0}
                placeholder={t('contractCyclesPlaceholder')}
                helperText={t('contractCyclesHelper')}
              />
            </Grid>
            {values.metadata?.cancelAfter && (
              <Grid size={{ xs: 12, md: 6 }}>
                <FormTextField
                  name="metadata.cancelAfterBehavior"
                  label={t('endOfContractBehavior')}
                  options={cancelBehaviors}
                />
              </Grid>
            )}
          </Grid>
        </>
      )}
    </Stack>
  );
};

export const SubscriptionPlanForm: React.FC<SubscriptionPlanFormProps> = ({
  plan,
  afterSave,
  children,
  isEditing = false,
}) => {
  const { session } = useSession();
  const { t } = useTranslation('translation', {
    keyPrefix: 'subscriptionPlan',
  });
  type StripePlanWithPartialMetadata = Omit<StripePlan, 'metadata'> & {
    metadata?: Partial<StripePlan['metadata']>;
  };

  const defaultValues: Partial<StripePlanWithPartialMetadata> = {
    name: '',
    amount: 0,
    interval: 'month',
    intervalCount: 1,
    active: true,
    currency: session.defaultCurrency,
    metadata: {
      cancelAfterBehavior: ContractEndBehavior.Cancel,
    },
    ...plan,
    // Convert amount from cents to dollars for display in form
    ...(plan?.amount && { amount: plan.amount / 100 }),
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t('nameRequired')),
    amount: Yup.number().required(t('amountRequired')).min(0),
    interval: Yup.string()
      .oneOf(['day', 'week', 'month', 'year'])
      .required(t('intervalRequired')),
    intervalCount: Yup.number().required(t('intervalCountRequired')).min(1),
    trialPeriodDays: Yup.number().min(0).nullable(),
    statementDescriptor: Yup.string().max(22).nullable(),
    description: Yup.string().nullable(),
    active: Yup.boolean(),
    taxCodeId: Yup.string().nullable(),
    metadata: Yup.object()
      .shape({
        billingCycleAnchor: Yup.string().nullable(),
        prorate: Yup.string()
          .oneOf(['prorate', 'no_prorate', 'no_charge'])
          .nullable(),
        noProrate: Yup.boolean().nullable(),
        cancelAfter: Yup.number().min(0).nullable(),
        cancelAfterBehavior: Yup.string()
          .oneOf(['cancel', 'auto_renew'])
          .nullable(),
        disableStripeTax: Yup.boolean().nullable(),
      })
      .nullable(),
  });

  const mutation = useMutation({
    mutationFn: async (values: Partial<StripePlan>) => {
      const amount = Number(values.amount);
      const payload = {
        ...values,
        amount: amount,
      };

      if (!payload.id) {
        delete payload.id;
      }

      if (isEditing) {
        return await updateStripePlan(plan?.id || '', payload);
      } else {
        return await createStripePlan(payload);
      }
    },

    onSuccess: async (data) => {
      if (afterSave) {
        await afterSave(data);
      }
    },
  });

  return (
    <Formik
      key={plan?.id || 'new'}
      initialValues={defaultValues}
      enableReinitialize={true}
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          await mutation.mutateAsync(values as Partial<StripePlan>);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {(formikProps) => (
        <Form>{children(formikProps as FormikProps<Partial<StripePlan>>)}</Form>
      )}
    </Formik>
  );
};

const intervalOptions = Object.entries(SubscriptionInterval).map(
  ([, value]) => ({
    value,
    label: SubscriptionIntervalDisplay[value as SubscriptionInterval],
  })
);

const behaviors = Object.entries(ProrationBehavior).map(([, value]) => ({
  value,
  label: ProrationBehaviorDisplay[value as ProrationBehavior],
}));

const cancelBehaviors = Object.entries(ContractEndBehavior).map(
  ([, value]) => ({
    value,
    label: ContractEndBehaviorDisplay[value as ContractEndBehavior],
  })
);

const getBehaviorText = (prorate?: ProrationBehavior, t?: any) => {
  if (!t) return '';

  if (prorate === ProrationBehavior.NoCharge) {
    return t('noChargeBehavior');
  } else if (prorate === ProrationBehavior.NoProrate) {
    return t('noProrateBehavior');
  } else {
    return t('prorateBehavior');
  }
};

const getAnchorValues = (interval?: string, t?: any) => {
  const values: Array<{ value: string; label: string }> = [];

  // Add "No Anchor" option as the first option for all intervals
  values.push({
    value: '',
    label: t ? t('noAnchor') : 'No Anchor',
  });

  if (interval === 'year') {
    return values; // handled by date picker
  } else if (interval === 'month') {
    for (let i = 1; i <= 28; i++) {
      values.push({ value: String(i), label: formatOrdinal(i) });
    }
    values.push({
      value: '-1',
      label: t ? t('endOfMonth') : 'end of the month',
    });
  } else if (interval === 'week') {
    values.push(
      { value: 'sunday', label: t ? t('sunday') : 'Sunday' },
      { value: 'monday', label: t ? t('monday') : 'Monday' },
      { value: 'tuesday', label: t ? t('tuesday') : 'Tuesday' },
      { value: 'wednesday', label: t ? t('wednesday') : 'Wednesday' },
      { value: 'thursday', label: t ? t('thursday') : 'Thursday' },
      { value: 'friday', label: t ? t('friday') : 'Friday' },
      { value: 'saturday', label: t ? t('saturday') : 'Saturday' }
    );
  }
  return values;
};
